import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  GraduationCap,
  Menu,
  Sparkles,
  X,
} from 'lucide-react';
import GastifyBrand from '../components/public/GastifyBrand';
import { todosLosModulos } from './academia/moduloData';
import { CRM_ORG_MODULE_ALIAS_MAP } from '../utils/accessModules';

const whatsappHref = 'https://wa.me/5493412739146';

const navigationItems = [
  { label: 'Volver al sistema', href: '/sistema/dashboard' },
  { label: 'Soporte', href: whatsappHref },
];

export default function AcademiaImpulza({ orgModules }) {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const hasOrgModuleAccess = (moduleId) => {
    if (!moduleId) return true;
    if (!orgModules) return true;

    const orgModulesSet = new Set(orgModules);
    const allowedModuleIds = [
      moduleId,
      ...(CRM_ORG_MODULE_ALIAS_MAP[moduleId] || []),
    ];
    return allowedModuleIds.some((candidateId) => orgModulesSet.has(candidateId));
  };

  const modulosVisibles = useMemo(
    () => todosLosModulos.filter((moduleItem) => hasOrgModuleAccess(moduleItem.requiredModule)),
    [orgModules],
  );

  const learningStats = useMemo(() => {
    const categoryTitles = new Set(
      modulosVisibles
        .map((moduleItem) => String(moduleItem.id || '').split('-')[0])
        .filter(Boolean),
    );

    return [
      {
        label: 'Guias activas',
        value: modulosVisibles.length,
      },
      {
        label: 'Areas cubiertas',
        value: categoryTitles.size,
      },
      {
        label: 'Soporte directo',
        value: 'WhatsApp',
      },
    ];
  }, [modulosVisibles]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[var(--gst-cream-50)] text-[var(--gst-graphite-900)]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[26rem] bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_48%),radial-gradient(circle_at_top_right,_rgba(254,215,170,0.24),_transparent_42%)]" />
      </div>

      <nav className="sticky top-0 z-40 border-b border-[color:var(--gst-border)] bg-[rgba(251,248,241,0.92)] backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Link to="/sistema/dashboard" className="min-w-0">
            <GastifyBrand
              iconSize={38}
              subtitle="Academia operativa"
            />
          </Link>

          <div className="hidden items-center gap-2 lg:flex">
            {navigationItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="rounded-md px-4 py-2 text-sm font-bold text-[var(--gst-graphite-700)] transition hover:bg-[var(--gst-surface)] hover:text-[var(--gst-graphite-900)]"
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/sistema/dashboard')}
              className="hidden rounded-md bg-[var(--gst-mint-500)] px-4 py-2 text-sm font-black text-white transition hover:bg-[var(--gst-mint-600)] md:inline-flex"
              type="button"
            >
              Ir al ERP
            </button>
            <button
              onClick={() => setIsMobileMenuOpen((currentValue) => !currentValue)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-[color:var(--gst-border)] bg-[var(--surface)] text-[var(--gst-graphite-700)] transition hover:border-[var(--gst-mint-500)] hover:text-[var(--gst-mint-700)] lg:hidden"
              type="button"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-30 transition ${
          isMobileMenuOpen ? 'pointer-events-auto bg-[rgba(31,36,33,0.18)] backdrop-blur-sm' : 'pointer-events-none opacity-0'
        } lg:hidden`}
      >
        <div className="ml-auto flex h-full w-[19rem] flex-col gap-3 border-l border-[color:var(--gst-border)] bg-[var(--gst-surface)] px-6 pb-8 pt-24 shadow-2xl">
          {navigationItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={closeMobileMenu}
              className="rounded-md border border-[color:var(--gst-border)] px-4 py-3 text-sm font-bold text-[var(--gst-graphite-700)] transition hover:border-[var(--gst-mint-500)] hover:text-[var(--gst-mint-700)]"
            >
              {item.label}
            </a>
          ))}
          <button
            type="button"
            onClick={() => {
              closeMobileMenu();
              navigate('/sistema/dashboard');
            }}
            className="rounded-md bg-[var(--gst-mint-500)] px-4 py-3 text-sm font-black text-white"
          >
            Volver al sistema
          </button>
        </div>
      </div>

      <main className="relative mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-8 md:px-6 md:py-10">
        <section className="grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_22rem]">
          <div className="rounded-[1.5rem] border border-[color:var(--gst-border)] bg-[var(--surface)] p-6 shadow-[var(--gst-shadow-md)] md:p-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--gst-mint-100)] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.24em] text-[var(--gst-mint-700)]">
              <GraduationCap size={14} />
              Academia Gastify
            </div>
            <h1 className="mt-5 max-w-3xl text-4xl font-black leading-[0.98] tracking-[-0.04em] text-[var(--gst-graphite-900)] md:text-6xl">
              Aprende a operar Gastify con el mismo criterio visual del sistema.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[var(--gst-graphite-700)] md:text-lg">
              Reunimos ventas, compras, inventario, finanzas, configuraciones y soporte en un centro de ayuda claro, limpio y alineado con la experiencia actual de Gastify.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to={modulosVisibles[0] ? `/sistema/academia/${modulosVisibles[0].id}` : '/sistema/dashboard'}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--gst-mint-500)] px-5 py-3 text-sm font-black text-white transition hover:bg-[var(--gst-mint-600)]"
              >
                Empezar recorrido
                <ArrowRight size={16} />
              </Link>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] px-5 py-3 text-sm font-black text-[var(--gst-graphite-700)] transition hover:border-[var(--gst-mint-500)] hover:text-[var(--gst-mint-700)]"
              >
                Hablar con soporte
              </a>
            </div>
          </div>

          <div className="grid gap-3">
            {learningStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-[1.25rem] border border-[color:var(--gst-border)] bg-[var(--gst-surface)] p-5 shadow-[var(--gst-shadow-sm)]"
              >
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[var(--gst-graphite-500)]">
                  {stat.label}
                </p>
                <p className="mt-3 text-3xl font-black text-[var(--gst-graphite-900)]">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-[1.5rem] border border-[color:var(--gst-border)] bg-[rgba(255,255,255,0.72)] p-5 shadow-[var(--gst-shadow-sm)] backdrop-blur-sm md:p-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[var(--gst-mint-700)]">
                Manuales activos
              </p>
              <h2 className="mt-2 text-2xl font-black text-[var(--gst-graphite-900)] md:text-3xl">
                Todo el conocimiento operativo, con iconos y estilo Gastify.
              </h2>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--gst-cream-100)] px-3 py-1.5 text-[11px] font-bold text-[var(--gst-graphite-700)]">
              <Sparkles size={14} className="text-[var(--gst-mint-600)]" />
              Experiencia unificada con la topbar
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {modulosVisibles.map((modulo) => {
              const Icon = modulo.icon;

              return (
                <Link
                  key={modulo.id}
                  to={`/sistema/academia/${modulo.id}`}
                  className="group flex h-full flex-col rounded-[1.25rem] border border-[color:var(--gst-border)] bg-[var(--surface)] p-5 shadow-[var(--gst-shadow-sm)] transition hover:-translate-y-1 hover:border-[var(--gst-mint-500)] hover:shadow-[var(--gst-shadow-md)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)] transition group-hover:bg-[var(--gst-mint-500)] group-hover:text-white">
                      <Icon size={22} />
                    </div>
                    <span className="rounded-full bg-[var(--gst-cream-50)] px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-500)]">
                      Modulo
                    </span>
                  </div>

                  <h3 className="mt-5 text-xl font-black leading-tight text-[var(--gst-graphite-900)]">
                    {modulo.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[var(--gst-graphite-700)]">
                    {modulo.description}
                  </p>

                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-black text-[var(--gst-mint-700)]">
                    Ver guia
                    <ArrowRight size={16} className="transition group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
